import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  throwError,
  BehaviorSubject,
  filter,
  take,
  switchMap,
  catchError,
} from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { ConnectionService } from '../services/connection.service';
import { NotificationService } from '../services/notification.service';
import { shouldPreserveReturnUrl } from '../../shared/utils/safe-return-url';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refreshInProgress = false;
  private readonly refreshDone$ = new BehaviorSubject<boolean | null>(null);

  public constructor(
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly connectionService: ConnectionService,
    private readonly notificationService: NotificationService,
    private readonly injector: Injector
  ) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = this.storageService.getToken();

    if (token) {
      request = this.withAuthHeader(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401(request, next, error);
        }
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  private handle401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    const requestUrl = error.url ?? '';
    const isPublicShare = requestUrl.includes('/edge-helpers/shared/');
    if (isPublicShare) {
      return throwError(() => error);
    }

    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/logout');

    if (isAuthEndpoint) {
      this.handleError(error);
      return throwError(() => error);
    }

    const refreshToken = this.storageService.getRefreshToken();
    if (!refreshToken) {
      this.handleError(error);
      return throwError(() => error);
    }

    if (this.refreshInProgress) {
      return this.refreshDone$.pipe(
        filter((done): done is boolean => done !== null),
        take(1),
        switchMap((ok) => {
          if (!ok) {
            return throwError(() => error);
          }
          const newToken = this.storageService.getToken();
          if (!newToken) {
            return throwError(() => error);
          }
          return next.handle(this.withAuthHeader(request, newToken));
        })
      );
    }

    this.refreshInProgress = true;
    this.refreshDone$.next(null);

    const authService = this.injector.get(AuthService);
    return authService.refresh().pipe(
      switchMap((response) => {
        this.refreshInProgress = false;
        this.refreshDone$.next(true);
        return next.handle(
          this.withAuthHeader(request, response.accessToken)
        );
      }),
      catchError((refreshError: unknown) => {
        this.refreshInProgress = false;
        this.refreshDone$.next(false);
        this.handleError(
          refreshError instanceof HttpErrorResponse
            ? refreshError
            : error
        );
        return throwError(() => refreshError);
      })
    );
  }

  private withAuthHeader(
    request: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.status === 0) {
      this.connectionService.setDisconnected();
      this.notificationService.showError(
        'Unable to connect to the server. Please check your connection.'
      );
      return;
    }

    if (error.status === 401) {
      const requestUrl = error.url ?? '';
      const isPublicShare = requestUrl.includes('/edge-helpers/shared/');
      if (isPublicShare) {
        return;
      }

      const hadSession = !!this.storageService.getToken();
      const isAuthAttempt =
        (error.url?.includes('/auth/login') ||
          error.url?.includes('/auth/register')) ??
        false;
      const currentUrl = this.router.url;
      this.storageService.clear();
      if (hadSession && !isAuthAttempt) {
        this.notificationService.showError(
          'Your session has expired. Please log in again.'
        );
      }

      if (!isAuthAttempt && shouldPreserveReturnUrl(currentUrl)) {
        void this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: currentUrl },
        });
      } else {
        void this.router.navigate(['/auth/login']);
      }
      return;
    }

    if (error.status === 403) {
      this.notificationService.showError(
        'Access denied. You do not have permission to access this resource.'
      );
      return;
    }

    if (error.status >= 500 && error.status < 600) {
      this.notificationService.showError(
        'Server error. Please try again later.'
      );
      return;
    }
  }
}
